import { useLocation } from "react-router-dom";
import { useSnackbarContext } from "./SnackbarContext.js";
import { usePrevious } from "./usePrevious.js";
import { useEffect } from "react";

/**
 * Reacts to client-side route changes by resetting the global snackbar so a
 * message from the previous page doesn't linger on the next one.
 *
 * Exception: when the destination carries an `authError` string in its
 * navigation state, surface that as an error snackbar instead of closing.
 * Pages that show a message and immediately redirect (e.g. `EmailSubscribe` →
 * `/signin` on an expired magic link) can't open the snackbar themselves —
 * this same route-change handler would close it on the very transition they
 * trigger. Carrying the message in `location.state` lets it survive the
 * navigation and render on the destination, where no further route change
 * wipes it.
 *
 * @returns {null}
 */
export const NavigationManager = () => {
    const location = useLocation();
    const previusPathname = usePrevious(location.pathname);
    const {
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        showSnackBar,
    } = useSnackbarContext();

    useEffect(() => {
        if (previusPathname === location.pathname) return;

        const authError = location.state?.authError;
        if (authError) {
            showSnackBar(
                setSnackbarOpen,
                setSnackbarMessage,
                setSnackbarSeverity,
                authError,
                "error",
            );
        } else {
            setSnackbarOpen(false); // Close snackbar on route change
        }
    }, [
        location.pathname,
        location.state,
        previusPathname,
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        showSnackBar,
    ]);

    return null;
}
